import { useIsMutating, useMutation } from '@tanstack/react-query';
import { Typography } from 'antd';
import {
  clearValidatedFieldErrors,
  gtinToUpc12,
  gtinToUpc13,
  upc12ToGTIN14,
  upc12ToUpc13,
  upc13ToGtin,
  upc13ToUpc12,
  upceToUPC12,
  upceToGTIN14,
} from 'common/components/product-add/components/utils/validation';

import { validateActiveProductViaForm } from 'services/product';
import { sleep } from 'utils/delay';

export const validateActiveProductViaFormKeys = [
  'validate-active-product-via-form',
];

export const lowercaseFirstChar = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str.charAt(0).toLowerCase() + str.slice(1);
};

/**
 * Get fields to auto-populate based on the validated source field
 * Matches the logic from GtinFieldset's handleValidateDigit
 * @param {string} sourceField - The field that was validated ('gtin', 'upc13', 'upc12', 'upc8')
 * @param {string} sourceValue - The value of the source field
 * @param {Object} currentValues - Current form values {gtin, upc13, upc12, upc8}
 * @returns {Object} - Fields to populate {gtin?, upc13?, upc12?, upc8?}
 */
export const getAutoPopulateFields = (
  sourceField,
  sourceValue,
  currentValues
) => {
  const fieldsToPopulate = {};

  switch (sourceField) {
    case 'gtin':
      // User input GTIN
      if (!currentValues.upc13 && sourceValue.startsWith('0')) {
        // If UPC-13 is empty and GTIN starts with 0: drop 1 leading zero
        const upc13 = gtinToUpc13(sourceValue);
        if (upc13) fieldsToPopulate.upc13 = upc13;
      }
      if (!currentValues.upc12 && sourceValue.startsWith('00')) {
        // If UPC-12 is empty and GTIN starts with 00: drop 2 leading zeroes
        const upc12 = gtinToUpc12(sourceValue);
        if (upc12) fieldsToPopulate.upc12 = upc12;
      }
      break;

    case 'upc13':
      // User input UPC-13
      if (!currentValues.upc12 && sourceValue.startsWith('0')) {
        // If UPC-12 is empty and UPC-13 starts with 0: drop 1 leading zero
        const upc12 = upc13ToUpc12(sourceValue);
        if (upc12) fieldsToPopulate.upc12 = upc12;
      }
      if (!currentValues.gtin) {
        // If GTIN is empty: prepend 1 zero
        const gtin = upc13ToGtin(sourceValue);
        if (gtin) fieldsToPopulate.gtin = gtin;
      }
      break;

    case 'upc12':
      // User input UPC-12
      if (!currentValues.upc13) {
        // If UPC-13 is empty: prepend 1 zero
        const upc13 = upc12ToUpc13(sourceValue);
        if (upc13) fieldsToPopulate.upc13 = upc13;
      }
      if (!currentValues.gtin) {
        // If GTIN is empty: prepend 2 zeroes
        const gtin = upc12ToGTIN14(sourceValue);
        if (gtin) fieldsToPopulate.gtin = gtin;
      }
      break;

    case 'upc8':
      // User input UPC-8 (UPC-E)
      if (!currentValues.upc12) {
        // Convert UPC-8 to UPC-12 (via UPC-E to UPC-A)
        const upc12 = upceToUPC12(sourceValue);
        if (upc12) {
          fieldsToPopulate.upc12 = upc12;

          // If UPC-13 is empty: convert UPC-8 to UPC-12 and prepend 1 zero
          if (!currentValues.upc13) {
            const upc13 = upc12ToUpc13(upc12);
            if (upc13) fieldsToPopulate.upc13 = upc13;
          }

          // If GTIN is empty: convert UPC-8 to UPC-12 and prepend 2 zeroes
          if (!currentValues.gtin) {
            const gtin = upceToGTIN14(sourceValue);
            if (gtin) fieldsToPopulate.gtin = gtin;
          }
        }
      }
      break;

    default:
      break;
  }

  return fieldsToPopulate;
};

export const setBarcodeAutoPopulateWarnings = async (
  form,
  fieldsToPopulate,
  sourceField
) => {
  const fieldNames = Object.keys(fieldsToPopulate);
  const sourceFieldUpper = sourceField?.toUpperCase();

  await sleep();

  const warnings = fieldNames.map((fieldName) => ({
    name: [fieldName],
    warnings: [
      <Typography.Text key={fieldName} style={{ color: '#faad14' }}>
        {fieldName.toUpperCase()} was auto-populated by {sourceFieldUpper}.
        Please verify this value
      </Typography.Text>,
    ],
  }));

  form.setFields(warnings);
};

const transformErrorsToFormFields = (errors) => {
  const transformedErrors = errors
    .filter((error) => error?.fieldName)
    .map((error) => ({
      name: [lowercaseFirstChar(error.fieldName)],
      errors: [error.errorMessage],
    }));

  // Check if BrickCode error exists
  const brickCodeError = errors.find(
    (error) => error?.fieldName?.toLowerCase() === 'brickcode'
  );

  // If BrickCode error exists, add an additional error for "brick"
  if (brickCodeError) {
    transformedErrors.push({
      name: ['brick'],
      errors: [brickCodeError.errorMessage],
    });
  }

  return transformedErrors;
};

export const handleBarcodeAutoPopulation = async (form, formErrors) => {
  // Get current values
  const gtin = form.getFieldValue('gtin');
  const upc13 = form.getFieldValue('upc13');
  const upc12 = form.getFieldValue('upc12');
  const upc8 = form.getFieldValue('upc8');

  const currentValues = { gtin, upc13, upc12, upc8 };

  // Check which fields have errors
  const hasGtinError = formErrors.some((error) =>
    error?.name?.includes('gtin')
  );
  const hasUpc13Error = formErrors.some((error) =>
    error?.name?.includes('upc13')
  );
  const hasUpc12Error = formErrors.some((error) =>
    error?.name?.includes('upc12')
  );
  const hasUpc8Error = formErrors.some((error) =>
    error?.name?.includes('upc8')
  );

  let fieldsToPopulate = {};
  let sourceField = '';

  // Priority order: UPC-8 → UPC-12 → UPC-13 → GTIN
  if (upc8 && !hasUpc8Error) {
    sourceField = 'upc8';
    const populatedFields = getAutoPopulateFields('upc8', upc8, currentValues);
    fieldsToPopulate = { ...fieldsToPopulate, ...populatedFields };
  } else if (upc12 && !hasUpc12Error) {
    sourceField = 'upc12';
    const populatedFields = getAutoPopulateFields(
      'upc12',
      upc12,
      currentValues
    );
    fieldsToPopulate = { ...fieldsToPopulate, ...populatedFields };
  } else if (upc13 && !hasUpc13Error) {
    sourceField = 'upc13';
    const populatedFields = getAutoPopulateFields(
      'upc13',
      upc13,
      currentValues
    );
    fieldsToPopulate = { ...fieldsToPopulate, ...populatedFields };
  } else if (gtin && !hasGtinError) {
    sourceField = 'gtin';
    const populatedFields = getAutoPopulateFields('gtin', gtin, currentValues);
    fieldsToPopulate = { ...fieldsToPopulate, ...populatedFields };
  }

  // Apply auto-populated fields
  if (Object.keys(fieldsToPopulate).length > 0) {
    form.setFieldsValue(fieldsToPopulate);
    await setBarcodeAutoPopulateWarnings(form, fieldsToPopulate, sourceField);
    return true;
  }

  return false;
};

// A field name can have multiple errors
export const mergeFormErrors = (formErrors) => {
  const errorMap = new Map();

  formErrors.forEach((item) => {
    const fieldName = item.name[0];

    if (errorMap.has(fieldName)) {
      // Add errors to existing field
      errorMap.get(fieldName).push(...item.errors);
    } else {
      // Create new entry
      errorMap.set(fieldName, [...item.errors]);
    }
  });

  // Convert Map back to array format
  return Array.from(errorMap.entries()).map(([fieldName, errors]) => ({
    name: [fieldName],
    errors:
      errors.length > 1
        ? [
            <div key={fieldName}>
              {errors.map((err, idx) => (
                <div key={idx}>{err}</div>
              ))}
            </div>,
          ]
        : errors,
  }));
};

export const useValidateActiveProductBase = (options = {}) => {
  const { onSuccess = () => {}, ...rest } = options;

  return useMutation({
    mutationKey: validateActiveProductViaFormKeys,
    mutationFn: async (params) => {
      await sleep(200);
      return validateActiveProductViaForm(params);
    },
    onSuccess,
    ...rest,
  });
};

export const useValidatePrimaryFieldsForActiveProduct = (options = {}) => {
  const { form, setValidateErrors, ...rest } = options;

  const handleSuccess = async (response) => {
    const activeResult = response?.data?.activeResult;
    const errors = activeResult?.errors || [];

    setValidateErrors(activeResult);
    const formErrors = transformErrorsToFormFields(errors);
    const mergedErrors = mergeFormErrors(formErrors);

    // Clear all validated field errors first
    clearValidatedFieldErrors(form);

    if (errors.length === 0) {
      await handleBarcodeAutoPopulation(form, formErrors);
      return;
    }

    if (formErrors.length === 0) {
      await handleBarcodeAutoPopulation(form, formErrors);
      return;
    }

    // Set backend errors on form
    await sleep();
    form.setFields(mergedErrors);

    // Attempt comprehensive auto-population even when errors exist
    // This allows any barcode field to populate others if they're valid
    await handleBarcodeAutoPopulation(form, formErrors);
  };

  return useValidateActiveProductBase({
    onSuccess: handleSuccess,
    ...rest,
  });
};

export const useValidateGtinUpc12 = (options = {}) => {
  return useValidateActiveProductBase(options);
};

export const useIsValidatingActiveProduct = () => {
  const isValidating = useIsMutating({
    mutationKey: validateActiveProductViaFormKeys,
  });

  return isValidating > 0;
};
